// src/store/reducers/faqReducer.js
import { FAQ_ACTIONS } from '../actions/actionTypes';

const initialState = {
  faqs: [],
  selectedFaq: null,
  loading: false,
  error: null,
  success: null,
  meta: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  },
  filters: {
    page: 1,
    limit: 20,
    search: '',
    status: '',
    categoryId: '',
    tagId: '',
    authorId: '',
    sortBy: 'priority',
    sortOrder: 'desc',
  },
  feedback: [],
  feedbackMeta: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  },
  relatedFaqs: [],
  categories: [],
  categoriesMeta: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  },
  tags: [],
  tagsMeta: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  },
  stats: null,
  uploadProgress: 0,
};

const faqReducer = (state = initialState, action) => {
  switch (action.type) {
    // Fetch FAQs
    case FAQ_ACTIONS.FETCH_FAQS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FAQ_ACTIONS.FETCH_FAQS_SUCCESS:
      return {
        ...state,
        loading: false,
        faqs: action.payload.data || [],
        meta: action.payload.meta || initialState.meta,
        error: null,
      };

    case FAQ_ACTIONS.FETCH_FAQS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        faqs: [],
      };

    // Fetch single FAQ
    case FAQ_ACTIONS.FETCH_FAQ_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FAQ_ACTIONS.FETCH_FAQ_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedFaq: action.payload,
        error: null,
      };

    case FAQ_ACTIONS.FETCH_FAQ_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        selectedFaq: null,
      };

    // Create FAQ
    case FAQ_ACTIONS.CREATE_FAQ_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case FAQ_ACTIONS.CREATE_FAQ_SUCCESS:
      return {
        ...state,
        loading: false,
        faqs: [action.payload, ...state.faqs],
        success: 'FAQ créée avec succès',
        error: null,
      };

    case FAQ_ACTIONS.CREATE_FAQ_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Update FAQ
    case FAQ_ACTIONS.UPDATE_FAQ_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case FAQ_ACTIONS.UPDATE_FAQ_SUCCESS:
      return {
        ...state,
        loading: false,
        faqs: state.faqs.map(faq =>
          faq.id === action.payload.id ? action.payload : faq
        ),
        selectedFaq: action.payload,
        success: 'FAQ mise à jour avec succès',
        error: null,
      };

    case FAQ_ACTIONS.UPDATE_FAQ_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Delete FAQ
    case FAQ_ACTIONS.DELETE_FAQ_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case FAQ_ACTIONS.DELETE_FAQ_SUCCESS:
      return {
        ...state,
        loading: false,
        faqs: state.faqs.filter(faq => faq.id !== action.payload.id),
        success: action.payload.message || 'FAQ supprimée avec succès',
        error: null,
      };

    case FAQ_ACTIONS.DELETE_FAQ_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Update FAQ status
    case FAQ_ACTIONS.UPDATE_FAQ_STATUS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FAQ_ACTIONS.UPDATE_FAQ_STATUS_SUCCESS:
      return {
        ...state,
        loading: false,
        faqs: state.faqs.map(faq =>
          faq.id === action.payload.id 
            ? { ...faq, status: action.payload.status }
            : faq
        ),
        selectedFaq: state.selectedFaq?.id === action.payload.id
          ? { ...state.selectedFaq, status: action.payload.status }
          : state.selectedFaq,
        success: `Statut mis à jour: ${action.payload.status}`,
        error: null,
      };

    case FAQ_ACTIONS.UPDATE_FAQ_STATUS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Fetch FAQ feedback
    case FAQ_ACTIONS.FETCH_FAQ_FEEDBACK_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FAQ_ACTIONS.FETCH_FAQ_FEEDBACK_SUCCESS:
      return {
        ...state,
        loading: false,
        feedback: action.payload.data || [],
        feedbackMeta: action.payload.meta || initialState.feedbackMeta,
        error: null,
      };

    case FAQ_ACTIONS.FETCH_FAQ_FEEDBACK_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        feedback: [],
      };

    // Fetch related FAQs
    case FAQ_ACTIONS.FETCH_RELATED_FAQS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FAQ_ACTIONS.FETCH_RELATED_FAQS_SUCCESS:
      return {
        ...state,
        loading: false,
        relatedFaqs: action.payload.data || [],
        error: null,
      };

    case FAQ_ACTIONS.FETCH_RELATED_FAQS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        relatedFaqs: [],
      };

    // Add related FAQ
    case FAQ_ACTIONS.ADD_RELATED_FAQ_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FAQ_ACTIONS.ADD_RELATED_FAQ_SUCCESS:
      return {
        ...state,
        loading: false,
        relatedFaqs: [...state.relatedFaqs, action.payload],
        success: 'FAQ associée avec succès',
        error: null,
      };

    case FAQ_ACTIONS.ADD_RELATED_FAQ_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Remove related FAQ
    case FAQ_ACTIONS.REMOVE_RELATED_FAQ_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FAQ_ACTIONS.REMOVE_RELATED_FAQ_SUCCESS:
      return {
        ...state,
        loading: false,
        relatedFaqs: state.relatedFaqs.filter(faq => faq.id !== action.payload.relatedFaqId),
        success: action.payload.message || 'Association supprimée',
        error: null,
      };

    case FAQ_ACTIONS.REMOVE_RELATED_FAQ_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Upload FAQ image
    case FAQ_ACTIONS.UPLOAD_FAQ_IMAGE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        uploadProgress: 0,
      };

    case FAQ_ACTIONS.UPLOAD_FAQ_IMAGE_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedFaq: {
          ...state.selectedFaq,
          images: [...(state.selectedFaq?.images || []), action.payload],
        },
        uploadProgress: 100,
        success: 'Image téléchargée avec succès',
        error: null,
      };

    case FAQ_ACTIONS.UPLOAD_FAQ_IMAGE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        uploadProgress: 0,
      };

    // Delete FAQ image
    case FAQ_ACTIONS.DELETE_FAQ_IMAGE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FAQ_ACTIONS.DELETE_FAQ_IMAGE_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedFaq: {
          ...state.selectedFaq,
          images: state.selectedFaq?.images?.filter(img => img.id !== action.payload.imageId),
        },
        success: action.payload.message || 'Image supprimée',
        error: null,
      };

    case FAQ_ACTIONS.DELETE_FAQ_IMAGE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Fetch FAQ categories
    case FAQ_ACTIONS.FETCH_FAQ_CATEGORIES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FAQ_ACTIONS.FETCH_FAQ_CATEGORIES_SUCCESS:
      return {
        ...state,
        loading: false,
        categories: action.payload.data || [],
        categoriesMeta: action.payload.meta || initialState.categoriesMeta,
        error: null,
      };

    case FAQ_ACTIONS.FETCH_FAQ_CATEGORIES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        categories: [],
      };

    // Fetch FAQ tags
    case FAQ_ACTIONS.FETCH_FAQ_TAGS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FAQ_ACTIONS.FETCH_FAQ_TAGS_SUCCESS:
      return {
        ...state,
        loading: false,
        tags: action.payload.data || [],
        tagsMeta: action.payload.meta || initialState.tagsMeta,
        error: null,
      };

    case FAQ_ACTIONS.FETCH_FAQ_TAGS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        tags: [],
      };

    // Fetch FAQ stats
    case FAQ_ACTIONS.FETCH_FAQ_STATS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FAQ_ACTIONS.FETCH_FAQ_STATS_SUCCESS:
      return {
        ...state,
        loading: false,
        stats: action.payload,
        error: null,
      };

    case FAQ_ACTIONS.FETCH_FAQ_STATS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        stats: null,
      };

    // Bulk delete
    case FAQ_ACTIONS.BULK_DELETE_FAQS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FAQ_ACTIONS.BULK_DELETE_FAQS_SUCCESS:
      return {
        ...state,
        loading: false,
        faqs: state.faqs.filter(faq => !action.payload.ids.includes(faq.id)),
        success: action.payload.message || 'FAQs supprimées avec succès',
        error: null,
      };

    case FAQ_ACTIONS.BULK_DELETE_FAQS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Set filters
    case FAQ_ACTIONS.SET_FAQ_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
      };

    // Clear states
    case FAQ_ACTIONS.CLEAR_FAQ_ERROR:
      return {
        ...state,
        error: null,
      };

    case FAQ_ACTIONS.CLEAR_FAQ_SUCCESS:
      return {
        ...state,
        success: null,
      };

    case FAQ_ACTIONS.CLEAR_SELECTED_FAQ:
      return {
        ...state,
        selectedFaq: null,
        feedback: [],
        relatedFaqs: [],
      };

    default:
      return state;
  }
};

export default faqReducer;